/**
 * Reusable footer component with legal links.
 *
 * Three variants:
 * - "full" (default): Logo + links + copyright on light backgrounds
 * - "minimal": Just links, centered (for auth pages)
 * - "dark": Dark themed for dark pages (landing page, etc.)
 *
 * The links come from config/site_chrome.json, which the Astro site (blog,
 * role pages) renders too, so both sites show the same footer.
 */

import { Link } from 'react-router-dom'
import logoPng from '../assets/fittohire-logo.svg'
import chrome from '@site-chrome'
import { useAuth } from '../hooks/useAuth'

interface FooterProps {
  variant?: 'full' | 'minimal' | 'dark'
  className?: string
}

type ChromeLink = { label: string; href: string; spa: boolean }

function FooterLink({ link, to, className }: { link: ChromeLink; to: string; className: string }) {
  // Blog and Roles are a separate site served from R2: a full page load, never
  // a client-side route the SPA does not have.
  return link.spa
    ? <Link to={to} className={className}>{link.label}</Link>
    : <a href={to} className={className}>{link.label}</a>
}

export default function Footer({ variant = 'full', className = '' }: FooterProps) {
  // "For Recruiters" sends a logged-in recruiter to their app, not the marketing
  // page they're already past. Logged-out / non-recruiters get the landing.
  const { isAuthenticated, user, planTier } = useAuth()
  const recruiterTo = isAuthenticated && (user?.persona === 'recruiter' || planTier === 'recruiter')
    ? '/dashboard/recruiter'
    : '/recruiters'
  const target = (link: ChromeLink) => (link.href === '/recruiters' ? recruiterTo : link.href)
  const year = new Date().getFullYear()

  if (variant === 'minimal') {
    return (
      <footer className={`border-t border-gray-200/50 py-4 mt-auto ${className}`}>
        {/* gray-600 base meets WCAG contrast on white; -gray-400 was a fail */}
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-center gap-6 text-sm text-gray-600">
          {chrome.footer.minimal_links.map((link) => (
            <FooterLink key={link.href} link={link} to={target(link)}
              className="inline-flex items-center hover:text-gray-900 transition-colors" />
          ))}
        </div>
      </footer>
    )
  }

  const dark = variant === 'dark'
  return (
    <footer
      className={`${dark ? 'border-t border-white/5' : 'border-t border-gray-200/50'} py-8 ${className}`}
      role="contentinfo"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
          <Link to="/" className="inline-flex items-center">
            <img src={logoPng} alt="FitToHire" className="h-10 block" />
          </Link>
          {/* gray-600 base for WCAG on light; was gray-400 (failed contrast) */}
          <nav
            className={`flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm ${dark ? 'text-gray-500' : 'text-gray-600'}`}
            aria-label="Footer navigation"
          >
            {chrome.footer.links.map((link) => (
              <FooterLink key={link.href} link={link} to={target(link)}
                className={`inline-flex items-center transition-colors ${dark ? 'hover:text-gray-300' : 'hover:text-gray-900'}`} />
            ))}
          </nav>
          <p className="text-sm text-gray-600">
            &copy; {year} {chrome.footer.copyright}
          </p>
        </div>
      </div>
    </footer>
  )
}
